import type { KeyboardEvent as ReactKeyboardEvent } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { Editor, type EditorHandle } from './Editor';
import { Preview, type PreviewHandle } from './Preview';
import { useStore } from '../store/useStore';
import { useT } from '../i18n/useT';

const MIN_RATIO = 0.2;
const MAX_RATIO = 0.8;

/**
 * The editor/preview surface. Handles both view modes, a draggable splitter,
 * and jitter-free synced scrolling driven by source-line anchors.
 */
export function SplitView({
  editorRef,
  onActivity,
}: {
  editorRef: React.RefObject<EditorHandle>;
  onActivity?: () => void;
}) {
  const t = useT();
  const viewMode = useStore((s) => s.viewMode);
  const switchPane = useStore((s) => s.switchPane);
  const content = useStore((s) => s.activeDoc.content);
  const docId = useStore((s) => s.activeDoc.id);
  const vimMode = useStore((s) => s.vimMode);
  const lineNumbers = useStore((s) => s.lineNumbers);
  const fontSize = useStore((s) => s.editorFontSize);
  const typewriter = useStore((s) => s.typewriter);
  const isNarrow = useStore((s) => s.isNarrow);
  const setContent = useStore((s) => s.setContent);

  const previewRef = useRef<PreviewHandle>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [ratio, setRatio] = useState(0.5);

  // Which surface currently drives the scroll — prevents feedback loops.
  const scrollLock = useRef<'editor' | 'preview' | null>(null);
  const lockTimer = useRef<number | null>(null);

  const releaseLockSoon = useCallback(() => {
    if (lockTimer.current) window.clearTimeout(lockTimer.current);
    lockTimer.current = window.setTimeout(() => {
      scrollLock.current = null;
    }, 80);
  }, []);

  const onEditorScroll = useCallback(() => {
    onActivity?.();
    if (viewMode !== 'split') return;
    if (scrollLock.current === 'preview') return;
    scrollLock.current = 'editor';
    const line = editorRef.current?.getTopLine() ?? 1;
    previewRef.current?.scrollToLine(line);
    releaseLockSoon();
  }, [viewMode, editorRef, releaseLockSoon, onActivity]);

  const onPreviewScroll = useCallback(() => {
    if (viewMode !== 'split') return;
    if (scrollLock.current === 'editor') return;
    scrollLock.current = 'preview';
    const line = previewRef.current?.getTopLine() ?? 1;
    editorRef.current?.scrollToLine(Math.round(line));
    releaseLockSoon();
  }, [viewMode, editorRef, releaseLockSoon]);

  // Splitter drag.
  const dragging = useRef(false);
  useEffect(() => {
    const onMove = (e: MouseEvent) => {
      if (!dragging.current || !containerRef.current) return;
      const rect = containerRef.current.getBoundingClientRect();
      const r = (e.clientX - rect.left) / rect.width;
      setRatio(Math.min(MAX_RATIO, Math.max(MIN_RATIO, r)));
    };
    const onUp = () => {
      dragging.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
    };
    window.addEventListener('mousemove', onMove);
    window.addEventListener('mouseup', onUp);
    return () => {
      window.removeEventListener('mousemove', onMove);
      window.removeEventListener('mouseup', onUp);
    };
  }, []);

  const startDrag = () => {
    dragging.current = true;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
  };

  // A `role="separator"` with `tabindex` promises keyboard operation; without
  // this handler the promise is empty and the split is mouse-only.
  const onSplitterKey = (e: ReactKeyboardEvent<HTMLDivElement>) => {
    const STEP = e.shiftKey ? 0.1 : 0.02;
    const clamp = (r: number) => Math.min(MAX_RATIO, Math.max(MIN_RATIO, r));
    if (e.key === 'ArrowLeft') setRatio((r) => clamp(r - STEP));
    else if (e.key === 'ArrowRight') setRatio((r) => clamp(r + STEP));
    else if (e.key === 'Home') setRatio(MIN_RATIO);
    else if (e.key === 'End') setRatio(MAX_RATIO);
    else if (e.key === 'Enter' || e.key === ' ') setRatio(0.5);
    else return;
    e.preventDefault();
  };

  const editorEl = (
    <Editor
      ref={editorRef}
      docId={docId}
      value={content}
      vimMode={vimMode}
      lineNumbers={lineNumbers}
      fontSize={fontSize}
      typewriter={typewriter}
      onChange={setContent}
      onScroll={onEditorScroll}
      onCursorActivity={onActivity}
    />
  );

  const previewEl = <Preview ref={previewRef} content={content} onScroll={onPreviewScroll} />;

  // Narrow viewports are always single-pane (no room for a usable split).
  if (viewMode === 'switch' || isNarrow) {
    return (
      <div className="mb-split" ref={containerRef}>
        <div className="mb-pane" style={{ display: switchPane === 'source' ? 'block' : 'none' }}>
          {editorEl}
        </div>
        <div className="mb-pane" style={{ display: switchPane === 'preview' ? 'block' : 'none' }}>
          {previewEl}
        </div>
      </div>
    );
  }

  return (
    <div className="mb-split" ref={containerRef}>
      <div className="mb-pane" style={{ flex: `${ratio} 1 0` }}>
        {editorEl}
      </div>
      <div
        className="mb-splitter"
        role="separator"
        aria-orientation="vertical"
        aria-label={t('split.resize')}
        aria-valuenow={Math.round(ratio * 100)}
        aria-valuemin={Math.round(MIN_RATIO * 100)}
        aria-valuemax={Math.round(MAX_RATIO * 100)}
        tabIndex={0}
        title={t('split.resize')}
        onMouseDown={startDrag}
        onKeyDown={onSplitterKey}
        onDoubleClick={() => setRatio(0.5)}
      />
      <div className="mb-pane" style={{ flex: `${1 - ratio} 1 0` }}>
        {previewEl}
      </div>
    </div>
  );
}
